import React, { useState, useEffect } from 'react';
import { FiCalendar, FiClock } from 'react-icons/fi';
import './DateTimePicker.css';

const MONTHS = [
  { value: '01', label: 'Jan' }, { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' }, { value: '04', label: 'Apr' },
  { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' }, { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' }, { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
];

// Generate year options (1900 to current year + 1)
const currentYear = new Date().getFullYear();
const YEARS = [];
for (let y = currentYear + 1; y >= 1900; y--) {
  YEARS.push(y);
}

// Days in a month
function getDaysInMonth(month, year) {
  if (!month || !year) return 31;
  return new Date(year, parseInt(month), 0).getDate();
}

/**
 * DateTimePicker - Custom dropdown-based date and time picker
 * Works consistently across all mobile devices.
 * Uses internal state for partial selections, only calls parent when complete.
 */
const DateTimePicker = ({
  date = '',
  time = '',
  onDateChange,
  onTimeChange,
  dateLabel = 'Date of Birth',
  timeLabel = 'Time of Birth',
  showDate = true,
  showTime = true,
}) => {
  // Internal state for partial date selections
  const [dateParts, setDateParts] = useState({ year: '', month: '', day: '' });
  const [timeParts, setTimeParts] = useState({ hour: '', minute: '', ampm: 'AM' });

  // Sync internal state from props (for pre-filled forms)
  useEffect(() => {
    if (date) {
      const [y, m, d] = date.split('-');
      setDateParts({ year: y || '', month: m || '', day: d || '' });
    }
  }, [date]);

  useEffect(() => {
    if (time) {
      const [h, m] = time.split(':');
      const h24 = parseInt(h);
      let hour12, ampm;
      if (h24 === 0) { hour12 = '12'; ampm = 'AM'; }
      else if (h24 < 12) { hour12 = String(h24); ampm = 'AM'; }
      else if (h24 === 12) { hour12 = '12'; ampm = 'PM'; }
      else { hour12 = String(h24 - 12); ampm = 'PM'; }
      setTimeParts({ hour: hour12, minute: m, ampm });
    }
  }, [time]);

  const daysInMonth = getDaysInMonth(dateParts.month, dateParts.year);

  const handleDatePart = (part, value) => {
    const updated = { ...dateParts, [part]: value };

    // Clamp day if month/year changed
    if (updated.year && updated.month && updated.day) {
      const maxDays = getDaysInMonth(updated.month, updated.year);
      if (parseInt(updated.day) > maxDays) {
        updated.day = String(maxDays).padStart(2, '0');
      }
    }

    setDateParts(updated);

    // Only notify parent when all parts are selected
    if (updated.year && updated.month && updated.day) {
      onDateChange(`${updated.year}-${updated.month}-${updated.day}`);
    }
  };

  const handleTimePart = (part, value) => {
    const updated = { ...timeParts, [part]: value };
    setTimeParts(updated);

    // Only notify parent when hour and minute are selected
    if (updated.hour && updated.minute) {
      let h24 = parseInt(updated.hour);
      if (updated.ampm === 'AM' && h24 === 12) h24 = 0;
      else if (updated.ampm === 'PM' && h24 !== 12) h24 += 12;
      onTimeChange(`${String(h24).padStart(2, '0')}:${updated.minute}`);
    }
  };

  return (
    <div className="dtp-wrapper">
      {showDate && (
        <div className="form-group">
          <label>
            <FiCalendar className="input-icon" /> {dateLabel}
          </label>
          <div className="dtp-row">
            <select
              className="dtp-select dtp-day"
              value={dateParts.day}
              onChange={(e) => handleDatePart('day', e.target.value)}
              required
            >
              <option value="">Day</option>
              {Array.from({ length: daysInMonth }, (_, i) => {
                const d = String(i + 1).padStart(2, '0');
                return <option key={d} value={d}>{i + 1}</option>;
              })}
            </select>

            <select
              className="dtp-select dtp-month"
              value={dateParts.month}
              onChange={(e) => handleDatePart('month', e.target.value)}
              required
            >
              <option value="">Month</option>
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>

            <select
              className="dtp-select dtp-year"
              value={dateParts.year}
              onChange={(e) => handleDatePart('year', e.target.value)}
              required
            >
              <option value="">Year</option>
              {YEARS.map(y => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showTime && (
        <div className="form-group">
          <label>
            <FiClock className="input-icon" /> {timeLabel}
          </label>
          <div className="dtp-row">
            <select
              className="dtp-select dtp-hour"
              value={timeParts.hour}
              onChange={(e) => handleTimePart('hour', e.target.value)}
              required
            >
              <option value="">Hr</option>
              {Array.from({ length: 12 }, (_, i) => {
                const h = i + 1;
                return <option key={h} value={String(h)}>{h}</option>;
              })}
            </select>

            <span className="dtp-colon">:</span>

            <select
              className="dtp-select dtp-minute"
              value={timeParts.minute}
              onChange={(e) => handleTimePart('minute', e.target.value)}
              required
            >
              <option value="">Min</option>
              {Array.from({ length: 60 }, (_, i) => {
                const m = String(i).padStart(2, '0');
                return <option key={m} value={m}>{m}</option>;
              })}
            </select>

            <select
              className="dtp-select dtp-ampm"
              value={timeParts.ampm}
              onChange={(e) => handleTimePart('ampm', e.target.value)}
              required
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
